import React, { useEffect, useState, useRef } from "react";
import { Download, FileDown } from "lucide-react";
import { Input, Modal, Button } from "antd";
import {
  useAllOrders,
  useDownloadLabel,
  useDownloadManifest,
  useDownloadStatusMutation,
} from "../../../../../api/orders/orders";
import { useLoader } from "../../../../../utils/LoaderContext";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const ORDERS_PER_PAGE = 20;

const PickUpPending = ({ status = "notpicked" }) => {
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrders, setSelectedOrders] = useState([]); // ✅ Store shipcluesId instead of awbNumber
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);

  const singleModalContentRef = useRef(null);
  const bulkModalContentRef = useRef(null);

  const { setLoading } = useLoader();

  const {
    data: allOrdersData,
    isLoading: isAllLoading,
    isError,
    refetch,
  } = useAllOrders({
    currentPage,
    ORDERS_PER_PAGE,
    status,
    search: searchQuery,
    downloadStatus: "not_downloded",
  });

  const { mutate: downloadLabel, isLoading: downloading } = useDownloadLabel();
  const { mutate: downloadManifest, isLoading: downloadingManifest } =
    useDownloadManifest();
  const { mutate: downloadStatus } = useDownloadStatusMutation();

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchQuery(searchText.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchText]);

  const orders = allOrdersData?.orders || [];
  const totalPages = allOrdersData?.totalPages || 1;
  const isLoading = isAllLoading;

  // ✅ Handle Select All - Store shipcluesId
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedOrders(
        orders
          .filter((order) => order.shipcluesId) // ✅ Only select orders with shipcluesId
          .map((order) => order.shipcluesId)
      );
    } else {
      setSelectedOrders([]);
    }
  };

  // ✅ Handle Single Select - Use shipcluesId
  const handleSelectOne = (shipcluesId) => {
    setSelectedOrders((prev) =>
      prev.includes(shipcluesId)
        ? prev.filter((id) => id !== shipcluesId)
        : [...prev, shipcluesId]
    );
  };

  // ✅ Download all selected labels using shipcluesId
  const handleDownloadAll = () => {
    downloadLabel(selectedOrders); // ✅ Pass array of shipcluesIds
  };

  const getSelectedOrdersData = () => {
    return orders.filter((order) => selectedOrders.includes(order.shipcluesId));
  };

  // --- Modal and PDF Logic ---
  const openModal = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedOrder(null);
  };

  const openBulkModal = () => {
    setIsBulkModalOpen(true);
  };

  const closeBulkModal = () => {
    setIsBulkModalOpen(false);
  };

  const handleDownloadSinglePDF = () => {
    if (!selectedOrder?.awbNumber) return;

    downloadStatus(
      { awbNumbers: [selectedOrder.awbNumber] },
      {
        onSuccess: async () => {
          if (singleModalContentRef.current) {
            setLoading(true);
            const canvas = await html2canvas(singleModalContentRef.current, {
              scale: 2,
              useCORS: true,
            });
            const imgData = canvas.toDataURL("image/png");

            const pdf = new jsPDF("p", "mm", "a4");
            const imgWidth = 210;
            const pageHeight = 295;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft >= 0) {
              position = heightLeft - imgHeight;
              pdf.addPage();
              pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
              heightLeft -= pageHeight;
            }

            pdf.save(
              `order_${selectedOrder?.awbNumber}_${new Date()
                .toISOString()
                .slice(0, 10)}.pdf`
            );
            setLoading(false);
          }
        },
      }
    );
  };

  const handleDownloadBulkPDF = () => {
    if (selectedOrders.length === 0) return;

    // ✅ Get AWB numbers from selected shipcluesIds
    const selectedAwbNumbers = orders
      .filter((order) => selectedOrders.includes(order.shipcluesId))
      .map((order) => order.awbNumber)
      .filter(Boolean);

    downloadStatus(
      { awbNumbers: selectedAwbNumbers },
      {
        onSuccess: async () => {
          if (bulkModalContentRef.current) {
            setLoading(true);
            const canvas = await html2canvas(bulkModalContentRef.current, {
              scale: 2,
              useCORS: true,
            });
            const imgData = canvas.toDataURL("image/png");

            const pdf = new jsPDF("p", "mm", "a4");
            const imgWidth = 210;
            const pageHeight = 295;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft >= 0) {
              position = heightLeft - imgHeight;
              pdf.addPage();
              pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
              heightLeft -= pageHeight;
            }

            pdf.save(
              `selected_orders_${new Date().toISOString().slice(0, 10)}.pdf`
            );
            setLoading(false);
          }
        },
      }
    );
  };

  // --- End Modal and PDF Logic ---

  if (isError)
    return <p className="text-red-600 mt-4">Error fetching orders</p>;

  return (
    <div className="w-full mt-6">
      {/* Search and Bulk Action Button - Fixed overflow */}
      <div className="mb-4 flex flex-col sm:flex-row sm:justify-between sm:items-center w-full gap-3">
        <Input
          className="w-full sm:w-80 flex-shrink-0"
          placeholder="Search by AWB, Order Number or Name"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
        {selectedOrders.length > 0 && (
          <div className="flex flex-wrap gap-2 flex-shrink-0">
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded disabled:opacity-50 text-sm whitespace-nowrap"
              onClick={handleDownloadAll}
              disabled={downloading}
            >
              {downloading
                ? "Downloading..."
                : `Download Labels (${selectedOrders.length})`}
            </button>
            <Button
              type="primary"
              onClick={openBulkModal}
              className="text-sm whitespace-nowrap"
              size="middle"
            >
              Packing List ({selectedOrders.length})
            </Button>
          </div>
        )}
      </div>

      {/* Table Container - Only table scrolls horizontally */}
      <div className="w-full border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1200px] w-full table-auto">
            <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700 sticky top-0">
              <tr>
                <th className="p-3">
                  <input
                    type="checkbox"
                    checked={
                      selectedOrders.length ===
                        orders.filter((o) => o.shipcluesId).length &&
                      orders.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                </th>
                <th className="p-3">Order Date</th>
                <th className="p-3">Manifest Id / Status</th>
                <th className="p-3">Order Number</th>
                <th className="p-3">Products</th>
                <th className="p-3">Customer / Contact</th>
                <th className="p-3">Address</th>
                <th className="p-3">Courier Details</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white text-sm">
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="text-center py-6 text-gray-500">
                    Loading orders...
                  </td>
                </tr>
              ) : orders.length > 0 ? (
                orders.map((order, index) => (
                  <tr
                    key={order.shipcluesId || index}
                    className="hover:bg-gray-50 border-b border-gray-100"
                  >
                    <td className="p-4">
                      {order.shipcluesId ? (
                        <input
                          type="checkbox"
                          checked={selectedOrders.includes(order.shipcluesId)}
                          onChange={() => handleSelectOne(order.shipcluesId)}
                        />
                      ) : (
                        <span className="text-gray-400 text-xs">N/A</span>
                      )}
                    </td>
                    <td className="p-4">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      <div className="font-medium text-blue-600">
                        {order.manifestId}
                      </div>
                      <div
                        className={`text-xs font-semibold px-2 py-1 rounded inline-block mt-1 ${
                          order.shippingStatus === "cancelled"
                            ? "bg-red-100 text-red-600"
                            : order.shippingStatus === "in_transit"
                            ? "bg-blue-100 text-blue-600"
                            : order.shippingStatus === "shipped"
                            ? "bg-green-100 text-green-600"
                            : "bg-yellow-100 text-yellow-600"
                        }`}
                      >
                        {order.shippingStatus}
                      </div>
                    </td>
                    <td className="p-4">{order.order_number}</td>
                    <td className="p-4">
                      {order.ProductDetails?.map((prod, i) => (
                        <p key={i} className="truncate">
                          {prod.name}
                        </p>
                      ))}
                    </td>
                    <td className="p-4">
                      <p>{order.CustomerName}</p>
                      <p>{order.BillingAddress?.phone}</p>
                    </td>
                    <td className="p-4 text-sm">
                      <p>{order.BillingAddress?.city}</p>
                      <p>{order.BillingAddress?.state}</p>
                      <p>{order.BillingAddress?.pincode}</p>
                    </td>
                    <td className="p-4 space-y-2">
                      <p className="text-sm font-medium text-gray-700">
                        {order?.courier}
                      </p>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-wrap gap-1">
                        {order.shipcluesId && (
                          <button
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm disabled:opacity-50"
                            onClick={() => downloadLabel([order.shipcluesId])} // ✅ Pass shipcluesId in array
                            disabled={downloading}
                            title="Download Label"
                          >
                            <Download size={16} />
                          </button>
                        )}
                        {order.manifestId && (
                          <button
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded text-sm disabled:opacity-50"
                            onClick={() => downloadManifest([order.manifestId])}
                            disabled={downloadingManifest}
                            title="Download Manifest"
                          >
                            <FileDown size={16} />
                          </button>
                        )}
                        <button
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-sm"
                          onClick={() => openModal(order)}
                          title="View Packing List"
                        >
                          Get List
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="text-center py-6 text-gray-400">
                    No orders found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination - Fixed positioning */}
      {!isLoading && orders.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-6 text-sm text-gray-700 gap-3">
          <p>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded border ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded border ${
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Single Order Product List Modal */}
      <Modal
        title={`Order No: ${selectedOrder?.awbNumber || ""}`}
        open={isModalOpen}
        onCancel={closeModal}
        footer={[
          <Button
            key="download-single"
            type="primary"
            onClick={handleDownloadSinglePDF}
          >
            Download as PDF
          </Button>,
          <Button key="close-single" onClick={closeModal}>
            Close
          </Button>,
        ]}
        width={800}
      >
        {selectedOrder && (
          <div
            ref={singleModalContentRef}
            className="border p-4 rounded-md shadow-sm"
          >
            <div className="flex justify-between ">
              <h3 className="font-semibold text-lg mb-2">
                Order: {selectedOrder.order_number}
              </h3>
              <h3 className="font-semibold text-lg mb-2">
                AWB No: {selectedOrder.awbNumber}
              </h3>
            </div>
            <div className="flex justify-between">
              <p className="mb-2 text-sm  font-semibold">
                Customer Name: {selectedOrder.CustomerName} | City:{" "}
                {selectedOrder.BillingAddress?.city}
              </p>
              <p className="mb-2 text-sm  font-semibold">
                Order Date:{" "}
                {new Date(selectedOrder.createdAt).toLocaleDateString()}
              </p>
            </div>

            <table className="w-full border border-gray-300 text-sm">
              <thead className="bg-gray-100 ">
                <tr>
                  <th className="p-2 border">#</th>
                  <th className="p-2 border">Product</th>
                  <th className="p-2 border">Qty</th>
                </tr>
              </thead>
              <tbody>
                {selectedOrder.ProductDetails?.map((prod, i) => (
                  <tr key={i} className="">
                    <td className="p-2 border">{i + 1}</td>
                    <td className="p-2 border">{prod.name}</td>
                    <td className="p-2 border">{prod.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      {/* Bulk Product List Modal */}
      <Modal
        title={`Products for Selected Orders (${selectedOrders.length})`}
        open={isBulkModalOpen}
        onCancel={closeBulkModal}
        footer={[
          <Button
            key="download-bulk"
            type="primary"
            onClick={handleDownloadBulkPDF}
          >
            Download as PDF
          </Button>,
          <Button key="close-bulk-modal" onClick={closeBulkModal}>
            Close
          </Button>,
        ]}
        width={800}
      >
        <div ref={bulkModalContentRef} className="space-y-4">
          {getSelectedOrdersData().map((order) => (
            <div
              key={order.shipcluesId}
              className="border p-4 rounded-md shadow-sm"
            >
              <div className="flex justify-between ">
                <h3 className="font-semibold text-lg mb-2">
                  Order: {order.order_number}
                </h3>
                <h3 className="font-semibold text-lg mb-2">
                  AWB No: {order.awbNumber}
                </h3>
              </div>
              <div className="flex justify-between">
                <p className="mb-2 text-sm  font-semibold">
                  Customer Name: {order.CustomerName} | City:{" "}
                  {order.BillingAddress?.city}
                </p>
                <p className="mb-2 text-sm  font-semibold">
                  Order Date: {new Date(order.createdAt).toLocaleDateString()}
                </p>
              </div>
              <table className="w-full border border-gray-300 text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 border">#</th>
                    <th className="p-2 border">Product</th>
                    <th className="p-2 border">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {order.ProductDetails?.map((prod, i) => (
                    <tr key={i}>
                      <td className="p-2 border">{i + 1}</td>
                      <td className="p-2 border">{prod.name}</td>
                      <td className="p-2 border">{prod.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default PickUpPending;